import { useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { joyApi } from "../api/joyApi";
import { speak } from "../hooks/useSpeech";
import AppShell from "../components/AppShell";
import JoyButton from "../components/JoyButton";
import LoadingScreen from "../components/LoadingScreen";
import ErrorState from "../components/ErrorState";
import Confetti from "../components/Confetti";

const norm = (s) => (s || "").toString().trim().toLowerCase();
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

export default function Quiz() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { userId } = useAuth();

  const quizzesQ = useQuery({ queryKey: ["quizzes", lessonId], queryFn: () => joyApi.quizzes(lessonId) });
  const lessonQ = useQuery({ queryKey: ["lesson", lessonId], queryFn: () => joyApi.lesson(lessonId) });

  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [feedback, setFeedback] = useState({ show: false, correct: null });
  const [answered, setAnswered] = useState(false);
  const [picked, setPicked] = useState(null);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const savedRef = useRef(false);

  // typing / drag / match local state
  const [typed, setTyped] = useState("");
  const [order, setOrder] = useState([]);
  const [matched, setMatched] = useState([]);
  const [selectedEn, setSelectedEn] = useState(null);
  const [wrongMatch, setWrongMatch] = useState(null);

  const quizzes = quizzesQ.data || [];
  const quiz = quizzes[idx];
  const total = quizzes.length;

  const ptShuffled = useMemo(
    () => (quiz?.pairs ? shuffle(quiz.pairs.map((p) => p.pt)) : []),
    [quiz?.id]
  );

  if (quizzesQ.isLoading || lessonQ.isLoading) return <LoadingScreen />;
  if (quizzesQ.isError)
    return (
      <AppShell>
        <ErrorState onRetry={quizzesQ.refetch} />
      </AppShell>
    );

  const resetQuestion = () => {
    setAnswered(false);
    setPicked(null);
    setTyped("");
    setOrder([]);
    setMatched([]);
    setSelectedEn(null);
    setWrongMatch(null);
  };

  const finish = (finalCorrect) => {
    const pct = Math.round((finalCorrect / total) * 100);
    setResult({ pct, correct: finalCorrect, total });
    setFinished(true);
    if (pct >= 50 && !savedRef.current) {
      savedRef.current = true;
      joyApi
        .completeLesson({
          user_id: userId,
          lesson_id: lessonId,
          xp_earned: lessonQ.data?.xp_reward || 0,
          correct_answers: finalCorrect,
          total_questions: total,
        })
        .then(() => qc.invalidateQueries({ queryKey: ["progress", userId] }))
        .catch(() => {});
    }
  };

  const advance = (isCorrect) => {
    setAnswered(true);
    setFeedback({ show: true, correct: isCorrect });
    const finalCorrect = correct + (isCorrect ? 1 : 0);
    if (isCorrect) setCorrect(finalCorrect);
    setTimeout(() => {
      setFeedback({ show: false, correct: null });
      resetQuestion();
      if (idx + 1 >= total) finish(finalCorrect);
      else setIdx(idx + 1);
    }, 1200);
  };

  const restart = () => {
    setIdx(0);
    setCorrect(0);
    setFinished(false);
    setResult(null);
    savedRef.current = false;
    resetQuestion();
  };

  // ----- Answer handlers -----
  const answerOption = (opt) => {
    if (answered) return;
    setPicked(opt);
    advance(norm(opt) === norm(quiz.correct_answer));
  };

  const submitTyping = () => {
    if (answered || !typed.trim()) return;
    advance(norm(typed) === norm(quiz.correct_answer));
  };

  const submitDrag = () => {
    if (answered || order.length !== quiz.options.length) return;
    advance(order.join(",") === quiz.correct_answer);
  };

  const tapMatchEn = (en) => {
    if (matched.includes(en)) return;
    setSelectedEn(en);
  };
  const tapMatchPt = (pt) => {
    if (!selectedEn) return;
    const pair = quiz.pairs.find((p) => p.en === selectedEn);
    if (pair && pair.pt === pt) {
      const nextMatched = [...matched, selectedEn];
      setMatched(nextMatched);
      setSelectedEn(null);
      if (nextMatched.length === quiz.pairs.length) advance(true);
    } else {
      setWrongMatch(pt);
      setTimeout(() => setWrongMatch(null), 500);
      setSelectedEn(null);
    }
  };

  // ===== RESULTS =====
  if (finished && result) {
    const passed = result.pct >= 50;
    const xpGained = passed ? lessonQ.data?.xp_reward || 0 : 0;
    return (
      <AppShell>
        {passed && <Confetti />}
        <div className="flex flex-col items-center text-center pt-10 joy-slide-in">
          <div className="text-7xl joy-bob">{passed ? "🎉" : "😢"}</div>
          <h1 className={`text-3xl font-black mt-3 ${passed ? "text-[#FF69B4]" : "text-[#5B8FB9]"}`}>
            {passed ? "Parabéns! 🌟" : "Tente novamente! 💪"}
          </h1>

          <div className="grid grid-cols-3 gap-3 w-full mt-6">
            <div className="bg-white rounded-[18px] py-4 border-2 border-[#FFE0EC]">
              <div className="text-2xl">✅</div>
              <div data-testid="result-correct" className="font-black text-[#4A3728] text-xl">{result.correct}/{result.total}</div>
              <div className="text-xs font-bold text-[#8B7355]">Acertos</div>
            </div>
            <div className="bg-white rounded-[18px] py-4 border-2 border-[#FFE0EC]">
              <div className="text-2xl">🌟</div>
              <div data-testid="result-percent" className="font-black text-[#4A3728] text-xl">{result.pct}%</div>
              <div className="text-xs font-bold text-[#8B7355]">Porcentagem</div>
            </div>
            <div className="bg-white rounded-[18px] py-4 border-2 border-[#FFE0EC]">
              <div className="text-2xl">⭐</div>
              <div data-testid="result-xp" className="font-black text-[#4A3728] text-xl">+{xpGained}</div>
              <div className="text-xs font-bold text-[#8B7355]">XP ganho</div>
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full mt-7">
            <JoyButton variant="pink" full data-testid="retry-quiz" onClick={restart}>🔄 Tentar novamente</JoyButton>
            <JoyButton variant="white" full data-testid="results-home" onClick={() => nav("/")}>🏠 Início</JoyButton>
            <JoyButton variant="blue" full data-testid="results-ranking" onClick={() => nav("/leaderboard")}>🏆 Ver Ranking</JoyButton>
          </div>
        </div>
      </AppShell>
    );
  }

  // ===== QUIZ RUNNING =====
  const optionClass = (opt) => {
    if (!answered) return "bg-white border-[#FFEAD6] text-[#4A3728]";
    const isCorrect = norm(opt) === norm(quiz.correct_answer);
    if (isCorrect) return "bg-[#E7F7E8] border-[#4CAF50] text-[#2E7D32]";
    if (opt === picked) return "bg-[#FDE4EC] border-[#E91E63] text-[#C2185B]";
    return "bg-white border-[#FFEAD6] text-[#4A3728] opacity-60";
  };

  return (
    <AppShell stars={false}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          data-testid="quiz-close"
          onClick={() => nav(`/lesson/${lessonId}`)}
          className="joy-press w-11 h-11 rounded-full bg-white flex items-center justify-center text-xl border-2 border-[#FFC0CB]"
          aria-label="Fechar"
        >
          ✕
        </button>
        <div className="flex-1 h-3 rounded-full bg-[#FFE0EC] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(idx / total) * 100}%`, background: "linear-gradient(90deg,#FF69B4,#FFD700)" }}
          />
        </div>
        <span data-testid="quiz-score" className="font-black text-[#4CAF50] whitespace-nowrap">✅ {correct}</span>
      </div>

      <div
        data-testid={`quiz-${quiz.question_type}`}
        className={`bg-white rounded-[26px] p-6 border-2 border-[#FFE0EC] ${feedback.show && !feedback.correct ? "joy-shake" : "joy-slide-in"}`}
        key={quiz.id}
        style={{ boxShadow: "0 10px 26px rgba(255,105,180,0.15)" }}
      >
        {quiz.image && quiz.question_type !== "match" && quiz.question_type !== "listening" && (
          <div className="text-6xl text-center mb-3">{quiz.image}</div>
        )}
        <p className="font-black text-[#4A3728] text-lg text-center mb-5">{quiz.question_pt}</p>

        {/* LISTENING */}
        {quiz.question_type === "listening" && (
          <button
            data-testid="listen-play"
            onClick={() => speak(quiz.question_en)}
            className="joy-press w-full rounded-[20px] py-6 mb-5 font-black text-white text-lg"
            style={{ background: "linear-gradient(135deg,#40E0D0,#5B8FB9)" }}
          >
            🎧 Toque para ouvir! 🔊
          </button>
        )}

        {/* MULTIPLE CHOICE / LISTENING / TRANSLATE options */}
        {["multiple_choice", "listening", "translate"].includes(quiz.question_type) && (
          <div className="grid grid-cols-1 gap-3">
            {quiz.options.map((opt) => (
              <button
                key={opt}
                data-testid={`quiz-option-${opt}`}
                disabled={answered}
                onClick={() => answerOption(opt)}
                className={`joy-press rounded-[18px] px-4 font-extrabold border-2 flex items-center justify-between ${optionClass(opt)}`}
                style={{ minHeight: 56 }}
              >
                <span>{opt}</span>
                {answered && norm(opt) === norm(quiz.correct_answer) && <span>✅</span>}
                {answered && opt === picked && norm(opt) !== norm(quiz.correct_answer) && <span>❌</span>}
              </button>
            ))}
          </div>
        )}

        {/* TYPING */}
        {quiz.question_type === "typing" && (
          <div>
            <input
              data-testid="typing-input"
              value={typed}
              disabled={answered}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitTyping()}
              placeholder="Digite sua resposta..."
              className={`w-full rounded-[18px] border-2 px-4 py-3 font-bold text-center text-lg outline-none ${
                answered
                  ? feedback.correct
                    ? "border-[#4CAF50] bg-[#E7F7E8]"
                    : "border-[#E91E63] bg-[#FDE4EC]"
                  : "border-[#FFC0CB] bg-[#FFF8E7] focus:border-[#FF69B4]"
              }`}
              style={{ minHeight: 56 }}
            />
            {answered && !feedback.correct && (
              <p className="text-center font-bold text-[#8B7355] mt-2 text-sm">Resposta: <b>{quiz.correct_answer}</b></p>
            )}
            <JoyButton variant="pink" full className="mt-4" disabled={answered || !typed.trim()} data-testid="typing-submit" onClick={submitTyping}>
              Verificar ✨
            </JoyButton>
          </div>
        )}

        {/* MATCH */}
        {quiz.question_type === "match" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
              <p className="text-center font-black text-[#4A3728] text-sm">🇺🇸 Inglês</p>
              {quiz.pairs.map((p) => {
                const done = matched.includes(p.en);
                const sel = selectedEn === p.en;
                return (
                  <button
                    key={p.en}
                    data-testid={`match-en-${p.en}`}
                    disabled={done}
                    onClick={() => tapMatchEn(p.en)}
                    className={`joy-press rounded-[16px] py-3 font-bold border-2 ${
                      done ? "bg-[#E7F7E8] border-[#4CAF50] text-[#2E7D32]" : sel ? "bg-[#FFE0EC] border-[#FF69B4]" : "bg-white border-[#FFEAD6]"
                    }`}
                  >
                    {p.en} {done && "✅"}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-center font-black text-[#4A3728] text-sm">🇧🇷 Português</p>
              {ptShuffled.map((pt) => {
                const done = matched.some((en) => quiz.pairs.find((p) => p.en === en)?.pt === pt);
                const wrong = wrongMatch === pt;
                return (
                  <button
                    key={pt}
                    data-testid={`match-pt-${pt}`}
                    disabled={done}
                    onClick={() => tapMatchPt(pt)}
                    className={`joy-press rounded-[16px] py-3 font-bold border-2 ${
                      done ? "bg-[#E7F7E8] border-[#4CAF50] text-[#2E7D32]" : wrong ? "bg-[#FDE4EC] border-[#E91E63] joy-shake" : "bg-white border-[#FFEAD6]"
                    }`}
                  >
                    {pt} {done && "✅"}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* DRAG / ORDER */}
        {quiz.question_type === "drag_drop" && (
          <div>
            <div className="min-h-[56px] rounded-[18px] border-2 border-dashed border-[#FFC0CB] bg-[#FFF8E7] p-2 flex flex-wrap gap-2 mb-3">
              {order.length === 0 && <span className="text-[#8B7355] font-bold text-sm self-center px-2">Toque nos itens na ordem correta 👇</span>}
              {order.map((o, i) => (
                <button
                  key={o}
                  disabled={answered}
                  onClick={() => setOrder(order.filter((x) => x !== o))}
                  className="joy-press rounded-[14px] px-3 py-2 font-bold bg-[#FF69B4] text-white text-sm"
                >
                  {i + 1}. {o}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {quiz.options.filter((o) => !order.includes(o)).map((o) => (
                <button
                  key={o}
                  data-testid={`drag-item-${o}`}
                  disabled={answered}
                  onClick={() => setOrder([...order, o])}
                  className="joy-press rounded-[14px] px-4 py-2.5 font-bold bg-white border-2 border-[#FFEAD6] text-[#4A3728]"
                >
                  {o}
                </button>
              ))}
            </div>
            {answered && !feedback.correct && (
              <p className="text-center font-bold text-[#8B7355] mt-2 text-sm">Ordem certa: <b>{quiz.correct_answer.replace(/,/g, " → ")}</b></p>
            )}
            <JoyButton variant="pink" full className="mt-4" disabled={answered || order.length !== quiz.options.length} data-testid="drag-submit" onClick={submitDrag}>
              Verificar ✨
            </JoyButton>
          </div>
        )}
      </div>

      {feedback.show && (
        <p className={`text-center font-black text-xl mt-5 joy-pop ${feedback.correct ? "text-[#4CAF50]" : "text-[#E91E63]"}`}>
          {feedback.correct ? "Muito bem! ✅🌟" : "Ops! ❌ Tente na próxima 💪"}
        </p>
      )}
    </AppShell>
  );
}
